import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  CheckCircle,
  ArrowUp,
} from "lucide-react";

// Testimonial Banner Component
const TestimonialBanner = ({
  name,
  company,
  quote,
  image
}: {
  name: string;
  company?: string;
  quote: React.ReactNode;
  image: string
}) => (
  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 py-8 my-8">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1200px]">
      <div className="flex flex-col md:flex-row items-center gap-8">
        <img
          src={image}
          alt={name}
          className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-lg flex-shrink-0"
        />
        <div className="flex-1">
          <p className="text-gray-700 dark:text-gray-300 text-lg md:text-xl italic leading-relaxed mb-4">
            &ldquo;{quote}&rdquo;
          </p>
          <div className="text-gray-900 dark:text-white font-semibold">
            {name}
            {company && <span className="text-indigo-600 dark:text-indigo-400 font-normal ml-2">— {company}</span>}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function OwnTheAgentPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    setShowBackToTop(scrollPosition > 300);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    handleScroll();

    const elementsToAnimate = document.querySelectorAll(".scroll-animate");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    elementsToAnimate.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [handleScroll]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 font-sans antialiased relative">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-indigo-700 via-blue-800 to-purple-900 text-white pt-24 md:pt-32 pb-40 md:pb-48 overflow-hidden">
        <div className="absolute inset-0 opacity-10 dark:opacity-5 bg-[url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2720%27 height=%2720%27 viewBox=%270 0 20 20%27%3E%3Cg fill=%27%23ffffff%27%3E%3Cpath fill-opacity=%270.4%27 d=%27M0 0 H 10 V 10 H 0 Z%27/%3E%3C/g%3E%3C/svg%3E')]"></div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px] z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 drop-shadow-lg leading-tight scroll-animate">
            Own the Agent™
          </h1>
          <p className="text-2xl md:text-3xl mb-6 text-white font-semibold scroll-animate" style={{ animationDelay: "0.1s" }}>
            Stop paying for tools you don&apos;t control.
            <br />Start building the ones that fit your business—fast.
          </p>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto scroll-animate" style={{ animationDelay: "0.2s" }}>
            Join a mastermind of <strong>15 business owners</strong> building AI systems together. Get production code for <strong>8020 Agent</strong> + <strong>8020 Brain</strong> (≈€50k value), weekly mastermind sessions, and 1-on-1 guidance. Expect <strong>~30% efficiency in a quarter</strong> with <strong>~60–90 min/week</strong>. Keep what you receive. Forever.
          </p>
          <p className="text-lg md:text-xl mb-10 text-white/80 max-w-3xl mx-auto scroll-animate italic" style={{ animationDelay: "0.3s" }}>
            You don&apos;t need to learn to code. You need to create the tools, intelligence, and systems your business needs—and I&apos;ll guide you through it in a small group of peers.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 mb-8 max-w-md mx-auto scroll-animate" style={{ animationDelay: "0.4s" }}>
            <Button asChild size="lg" className="w-full md:w-auto px-10 py-6 bg-white text-indigo-700 font-semibold hover:bg-white/90 transition-colors duration-200 shadow-xl hover:shadow-2xl text-lg transform hover:scale-105">
              <Link to="/ota-application">See if you qualify →</Link>
            </Button>
            <p className="text-white/80 text-sm">Limited to 15 members • Typical payback 3–6 months</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-24 md:h-32 lg:h-40 text-white dark:text-gray-950 overflow-hidden">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="h-full w-full fill-current">
            <path d="M0,100 C480,0 960,0 1440,100 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </div>

      {/* Why Now Section */}
      <section className="bg-white dark:bg-gray-950 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white scroll-animate">
            Why Now
          </h2>
          <div className="scroll-animate space-y-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            <p>You don&apos;t need more hours—you need a system that multiplies the ones you have.</p>

            <p>This isn&apos;t about learning to code. It&apos;s about creating the <strong>tools, intelligence, and systems</strong> your business needs. With modern AI assistants and production code to start from, you can build what used to require a dev team.</p>

            <p><strong>This is a mastermind, not a course.</strong> Join 15 business owners in weekly sessions where you show your work, solve problems together, and push each other forward. Get unstuck fast by learning from peers who&apos;ve likely been through what you&apos;re facing.</p>

            <p><strong>Ship your first win in 2–4 weeks:</strong> Start from production code, not a blank page. Use <strong>8020 Agent</strong> for Google Ads intelligence and <strong>8020 Brain</strong> as your business operating system—ingesting emails, files, meeting transcripts, SOPs, pricing docs, and customer data to accelerate every decision.</p>

            <p><strong>~30% efficiency within a quarter:</strong> Automate the repetitive 30%—reports, pricing checks, lead triage, follow-ups.</p>

            <p><strong>Grow without adding headcount:</strong> Replace &ldquo;hire to keep up&rdquo; with &ldquo;automate to keep up.&rdquo;</p>

            <p><strong>Pull ahead on execution:</strong> Faster quotes, proactive alerts, cleaner handoffs—things clients feel immediately.</p>

            <p><strong>What it asks of you:</strong> 60–90 minutes a week.<br />
            <strong>What it returns:</strong> more capacity, steadier delivery, and margin that compounds.</p>

            <p>Clients increasingly ask, &ldquo;Show me your system.&rdquo;<br />
            A purpose-built agent, dashboards, and alerts turn &ldquo;we&apos;re good&rdquo; into &ldquo;we&apos;re clearly better.&rdquo;</p>

            <p><strong>A year from now, this will be table stakes.</strong> The owners who start now look like outliers for the next 12 months—and set the pace after that.</p>
          </div>

          {/* Proof Strip */}
          <div className="mt-8 scroll-animate">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-200 dark:border-indigo-800 rounded-lg py-4 px-6">
              <p className="text-center text-sm md:text-base text-gray-700 dark:text-gray-300">
                <strong>3–6 month payback (member data)</strong> • <strong>~30% efficiency in a quarter</strong> • <strong>60–90 min/wk</strong>
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 flex justify-center scroll-animate">
            <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg">
              <Link to="/ota-application">See if you qualify →</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonial 1 - Kuda (after Why Now) */}
      <TestimonialBanner
        name="Kudkwashe Chinhara"
        quote={<>Soon after finishing Build The Agent, I unexpectedly landed my first Automation/AI retainer. Mike advised me how to price the deal and how to prepare for it. Soon after, Own The Agent was launched. I made sure I got a spot because <strong>the potential LTV from that one project was over 3,200% the membership price.</strong> So let&apos;s call it a negative payback period. And yes, more client projects have followed and one of my apps won the Innovation Pioneer award from The PPC Hub. <strong>Own The Agent is now an integral part of my business.</strong> It has enabled the creation of <strong>an income stream that was not even on my radar.</strong></>}
        image="/images/kuda.jpeg"
      />

      {/* What Makes This Different */}
      <section className="bg-gray-50 dark:bg-gray-900 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white scroll-animate">
            What Makes This Different
          </h2>

          {/* Working Code */}
          <div className="mb-16 scroll-animate">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">1) You Start With Working Code (€50,000 Value)</h3>
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div>
                <h4 className="font-semibold text-xl mb-3 text-indigo-600 dark:text-indigo-400">8020 Agent (full source code)</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Production Google Ads intelligence platform. Turn raw data into insights + AI analysis. Fork it day one and skip 3-6 months of rebuild. The same system handling reporting for agencies managing millions in ad spend.</p>
                <img src="/images/8020agent.png" alt="8020 Agent Screenshot" className="rounded-lg border border-gray-200 dark:border-gray-700 w-full" />
              </div>
              <div>
                <h4 className="font-semibold text-xl mb-3 text-purple-600 dark:text-purple-400">8020 Brain (full source code + ongoing updates)</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Your business operating system. Ingests emails, files, meeting transcripts, SOPs, pricing docs, customer data. Accelerates decisions, content creation, development, and product ideas. Another 2-3 months of dev work you skip entirely.</p>
                <img src="/images/8020brain.png" alt="8020 Brain Screenshot" className="rounded-lg border border-gray-200 dark:border-gray-700 w-full" />
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300 font-semibold">Both systems. Full source code. Yours to modify, deploy, and use internally. Forever.</p>
            <p className="text-gray-600 dark:text-gray-400 mt-2 italic">Most dev teams would quote €25,000+ per system. You get both, plus ongoing updates during your term, plus the guidance to actually use them.</p>
          </div>

          {/* Mastermind Not Course */}
          <div className="mb-16 scroll-animate">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">2) This Is a Mastermind, Not a Course</h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">I&apos;m not here to build your tools for you. I&apos;m here to help you build what you&apos;re actually capable of building.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="font-semibold mb-2 text-gray-900 dark:text-white">My role:</p>
                <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                  <li>• <strong>Accountability</strong> — Weekly check-ins mean you can&apos;t ghost your own projects</li>
                  <li>• <strong>Sounding board</strong> — Discuss architecture, pricing, strategy before you commit</li>
                  <li>• <strong>Shortcut provider</strong> — I&apos;m not a professional developer, but I&apos;ve been where you are and can help you find ways around problems</li>
                  <li>• <strong>Unblocker</strong> — When you&apos;re stuck, we get you unstuck</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2 text-gray-900 dark:text-white">Your role:</p>
                <p className="text-gray-600 dark:text-gray-400">Show up. Build. Ship.</p>
                <p className="text-gray-700 dark:text-gray-300 mt-4">This isn&apos;t a structured program with deliverables. It&apos;s a working group where serious operators help each other build serious tools.</p>
              </div>
            </div>
          </div>

          {/* Small Group */}
          <div className="mb-16 scroll-animate">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">3) Small Group, Real Momentum (Max 15)</h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg">Weekly sessions where you show what you&apos;re working on, get feedback, solve problems together, and move forward.</p>
            <p className="text-gray-700 dark:text-gray-300 mt-4">Operators (mostly 6-7 figures) solving similar problems. Some three months ahead of you, some three months behind.</p>
            <p className="text-gray-700 dark:text-gray-300 font-semibold mt-2">Everyone pushing forward together.</p>
          </div>

          {/* Direct Access */}
          <div className="scroll-animate">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">4) Direct Access When You Need It</h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg">17 one-on-one sessions annually for annual members. Bring your whole team if you want.</p>
            <p className="text-gray-700 dark:text-gray-300 mt-4">These are your calls for your business. Strategy, architecture, pricing, problem-solving. Whatever you need.</p>
          </div>
        </div>
      </section>

      {/* Testimonial 2 - David (at 1/3 through page) */}
      <TestimonialBanner
        name="David Browne"
        company="The Scottish Shutter Company"
        quote={<>The Own the Agent programme has delivered <strong>the fastest and most significant return on investment</strong> I&apos;ve ever seen from any mastermind. Before OTA, I used ChatGPT to write blogs and emails. After OTA, I was developing advanced pricing applications that now save our company hours of work every single week. Mike&apos;s teaching opened up an entirely new toolkit that I&apos;d never even heard of before. <strong>My background is in sales and marketing, not software development,</strong> so if I can build the kind of tools I now use daily, the potential for others is enormous. OTA fundamentally changes how you think and how you communicate. It sharpens your questioning and gives you the confidence to build systems that transform your business. It&apos;s a serious investment, but <strong>the value you get back is extraordinary.</strong></>}
        image="/images/david.jpeg"
      />

      {/* What You're Building */}
      <section className="bg-white dark:bg-gray-950 py-12 md:py-16" id="what-youre-building">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white scroll-animate">
            What You&apos;re Actually Building
          </h2>
          <p className="text-center text-lg text-gray-700 dark:text-gray-300 mb-8 scroll-animate">
            Start with 1–2 high-leverage workflows that return time fast; stack from there.
          </p>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-8 scroll-animate">Members are building:</p>
          <ul className="grid md:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300 scroll-animate">
            <li>• PMax insight dashboards with proactive alerts</li>
            <li>• Lead triage and qualification systems</li>
            <li>• Client KPI dashboards that prevent churn</li>
            <li>• Budget monitoring and anomaly detection</li>
            <li>• Pricing calculators that close more deals</li>
            <li>• AI-powered content acceleration tools</li>
            <li>• Custom integrations nobody else has</li>
          </ul>
          <p className="text-center text-lg text-gray-700 dark:text-gray-300 mt-8 scroll-animate">
            Most members ship the first workflow in 2–4 weeks and stack from there to ~30% efficiency within a quarter.
          </p>
          <p className="text-center text-xl font-semibold text-gray-900 dark:text-white mt-6 scroll-animate">
            You decide what to build. We help you actually build it.
          </p>
        </div>
      </section>

      {/* ROI Table */}
      <section className="bg-gray-50 dark:bg-gray-900 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white scroll-animate">
            How This Pays For Itself (No Hype)
          </h2>
          <div className="overflow-x-auto scroll-animate">
            <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-indigo-600 text-white">
                  <th className="p-4 text-left">Path</th>
                  <th className="p-4 text-left">Example</th>
                  <th className="p-4 text-left">Annual Impact</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-4 font-semibold">Efficiency</td>
                  <td className="p-4">~30% uplift on a €70k cost base</td>
                  <td className="p-4 font-bold text-green-600">€21,000</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-4 font-semibold">Time Saved</td>
                  <td className="p-4">2 hrs/week at €200/hr</td>
                  <td className="p-4 font-bold text-green-600">€20,800/year</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-4 font-semibold">Client Retention</td>
                  <td className="p-4">Keep 1 × €2,000/mo client 12 months longer</td>
                  <td className="p-4 font-bold text-green-600">€24,000</td>
                </tr>
                <tr>
                  <td className="p-4 font-semibold">Option Value</td>
                  <td className="p-4">Skip ~€50,000 dev via production code</td>
                  <td className="p-4 font-bold text-green-600">€50,000</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center mt-8 text-lg text-gray-700 dark:text-gray-300 scroll-animate">
            Most members hit meaningful savings or revenue impact in 3–6 months with 60–90 minutes/week.
          </p>
          <p className="text-center mt-4 text-gray-600 dark:text-gray-400 scroll-animate">
            <strong>Proof:</strong> David built a pricing calculator saving &ldquo;dozens of hours monthly.&rdquo; The program paid for itself within three months. Kuda&apos;s first AI retainer delivered over 3,200% ROI on the membership.
          </p>
        </div>
      </section>

      {/* Testimonial 3 - Paul (at 2/3 through page) */}
      <TestimonialBanner
        name="Paul Saunders"
        quote={<>2025 has been an incredible journey, and it&apos;s all because of Mike. <strong>What sets him apart is that he doesn&apos;t hold back.</strong> He shares everything, lays it all out there, and genuinely wants to see us succeed. There are no secrets. He cares about what we&apos;re all building and is committed to helping us get there. By being part of OTA and absorbing his mindset, I&apos;ve discovered what&apos;s truly possible. <strong>I&apos;m building apps, dashboards and AI workflows with zero prior experience</strong> as a complete no-coder. That kind of generosity and transparency changes everything. I&apos;m genuinely excited about what&apos;s next.</>}
        image="/images/paul.jpeg"
      />

      {/* Investment Options */}
      <section className="bg-white dark:bg-gray-950 py-12 md:py-16" id="investment">
        <div className="container mx-auto px-4 max-w-[1360px]">
          <div className="text-center max-w-3xl mx-auto mb-12 scroll-animate">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Investment Options</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mx-auto scroll-animate">

            {/* Annual */}
            <Card className="flex flex-col h-full border-2 border-indigo-500 dark:border-indigo-400 bg-white dark:bg-gray-900 shadow-xl overflow-hidden relative">
              <div className="absolute -right-10 top-6 rotate-45 bg-indigo-600 text-white px-10 py-1 text-sm font-bold">BEST VALUE</div>
              <CardHeader className="bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-800">
                <CardTitle className="text-center space-y-2 pt-6">
                  <div className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold mb-1">Annual</div>
                  <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">€11,999</div>
                  <div className="text-sm text-muted-foreground">(€1,000/month)</div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                <ul className="space-y-3">
                  {[
                    "Full year of support + code updates",
                    "17 one-on-one sessions (bring your whole team)",
                    "45 weekly mastermind sessions",
                    "Full source code (both systems) + all updates released during year",
                    "Priority access to new patterns and features",
                    "2026 Annual Retreat ticket included (location TBD, alternating AU/EU)",
                    "Save €17,889 vs monthly option"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="mt-auto bg-indigo-50 dark:bg-indigo-950/30 border-t border-indigo-100 dark:border-indigo-800 p-6">
                <div className="w-full space-y-3">
                  <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700 py-5 text-lg">
                    <Link to="/ota-application">Apply Now</Link>
                  </Button>
                  <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                    Typical payback 3–6 months with ~60–90 min/week.
                  </p>
                </div>
              </CardFooter>
            </Card>

            {/* Six-Month */}
            <Card className="flex flex-col h-full bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
              <CardHeader className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                <CardTitle className="text-center space-y-2 pt-6">
                  <div className="text-sm text-purple-600 dark:text-purple-400 font-semibold mb-1">Six-Month</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">€8,999</div>
                  <div className="text-sm text-muted-foreground">(€1,500/month equivalent)</div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                <ul className="space-y-3">
                  {[
                    "6 months of support + code updates",
                    "8 one-on-one sessions",
                    "23 weekly mastermind sessions",
                    "Full source code + updates during term",
                    "Save €6,495 vs monthly option",
                    "Annual Retreat: Optional ticket purchase (€2,500)"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="mt-auto bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 p-6">
                <div className="w-full space-y-3">
                  <Button asChild className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 text-lg">
                    <Link to="/ota-application">Apply Now</Link>
                  </Button>
                  <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                    Typical payback 3–6 months with ~60–90 min/week.
                  </p>
                </div>
              </CardFooter>
            </Card>

            {/* Monthly */}
            <Card className="flex flex-col h-full bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
              <CardHeader className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                <CardTitle className="text-center space-y-2 pt-6">
                  <div className="text-sm text-gray-700 dark:text-gray-300 font-semibold mb-1">Monthly</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">€2,499</div>
                  <div className="text-sm text-muted-foreground">per month</div>
                  <div className="text-xs text-muted-foreground">(3-month minimum)</div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                <ul className="space-y-3">
                  {[
                    "Rolling monthly commitment after initial 3 months",
                    "One-on-one session every 3 weeks",
                    "Weekly mastermind sessions",
                    "Full source access during active term",
                    "Minimum: €7,497",
                    "Annual Retreat: Optional ticket purchase (€5,000)"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="mt-auto bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-center p-6">
                <Button asChild variant="outline" className="w-full border-indigo-300 text-indigo-600 hover:bg-indigo-100/50 py-5 text-lg">
                  <Link to="/ota-application">Apply Now</Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* Straight Talk Warning */}
          <div className="mt-16 max-w-3xl mx-auto scroll-animate">
            <Card className="bg-yellow-50 dark:bg-yellow-950/30 border-2 border-yellow-200 dark:border-yellow-800">
              <CardContent className="p-8">
                <p className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 text-center">
                  Straight talk: If €12,000 feels heavy or you&apos;re not profitable yet, this isn&apos;t the right time. This is for operators ready to invest and ship, not window shoppers.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400 scroll-animate">
            <p><strong>Build the Agent Workshop Alumni:</strong> Apply your €500 credit to any option.</p>
          </div>
        </div>
      </section>

      {/* Internal-Use License & Full Package */}
      <section className="bg-gray-50 dark:bg-gray-900 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Internal-Use License */}
            <div className="scroll-animate">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Internal-Use License (Plain English)</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>Use the code however you want <strong>inside your business</strong>. Modify it. Deploy it. Use it to deliver services to clients (without giving them the code).</p>
                <p>You keep all versions received during your term. <strong>Forever.</strong></p>
                <p>You stop receiving updates when your membership ends. Active members get new features, improvements, and bug fixes. Former members have a permanent snapshot they can use indefinitely.</p>
                <p>You cannot resell the code, redistribute it, or package it as a competing product. Everything else is fair game.</p>
              </div>
            </div>

            {/* Full Package Details */}
            <div className="scroll-animate">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Full Package Details</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">Code &amp; Infrastructure:</h3>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li>✅ 8020 Agent (full source code)</li>
                    <li>✅ 8020 Brain (full source + updates during your term)</li>
                    <li>✅ Internal-use license (modify, deploy, use with clients)</li>
                    <li>✅ Ongoing code improvements throughout your term</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">Live Support:</h3>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li>✅ 45 weekly mastermind sessions (show your work, get feedback, move forward)</li>
                    <li>✅ Join a group of experienced business owners who&apos;ve likely been through what you&apos;re facing and can offer help, advice, and assistance</li>
                    <li>✅ One-on-one sessions (17/year for annual, 8 for six-month, 1 every 3 weeks for monthly)</li>
                    <li>✅ Private group chat (fast answers, shared solutions)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">Resources:</h3>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li>✅ Pattern library (prompts, agents, evals, SOPs)</li>
                    <li>✅ Priority access to new patterns and features (annual members)</li>
                    <li>✅ Annual retreat access (included for annual, optional for others)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white dark:bg-gray-950 py-12 md:py-16" id="faq">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white scroll-animate">
            Common Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4 scroll-animate">
            {[
              {
                question: "Is this only for technical people?",
                answer: "No. David Browne's background is in sales and marketing. He's now building pricing applications that save his company dozens of hours per week.\n\nPaul Saunders describes himself as a \"complete no-coder\" who's now building apps, dashboards, and AI workflows.\n\nThe technical barriers have largely disappeared with modern AI coding assistants. What matters more is clear thinking about what problems to solve, willingness to learn and debug, and persistence when things don't work the first time.\n\nIf you can describe what you want clearly and stick with it when things get messy (they will), you can build remarkable tools."
              },
              {
                question: "What if I don't have time to implement?",
                answer: "Plan on one focused hour per week. Most members ship their first workflow in 2–4 weeks and recover the hour within the first quarter.\n\nThen don't join if you can't commit that. Seriously.\n\nThis is designed for people who will carve out 1-2 hours weekly to build. If you can't commit that, you'll waste your money and my time.\n\nBut here's the thing: most members find they're gaining time within a few months because the tools they build start paying dividends. The time investment is front-loaded. The time savings compound.\n\nAsk yourself: Are you willing to invest 5% of your resources now to protect 100% of your business? Because that's the actual choice."
              },
              {
                question: "What exactly is your role? Are you building this for me?",
                answer: "No. I'm not building your tools for you.\n\nI'm your:\n• Accountability partner — Weekly sessions mean you can't ghost your own projects\n• Sounding board — Discuss ideas before you waste time building the wrong thing\n• Shortcut provider — I've been where you are. I can help you avoid dead ends\n• Unblocker — When you're stuck, we get you unstuck\n\nI'm not a professional software developer. But I've built plenty of tools and can help you find ways around problems faster than you'd figure them out alone.\n\nYou do the building. I help you actually finish what you start."
              },
              {
                question: "Can I bring my team into the sessions?",
                answer: "One-on-ones: Bring anyone you want. These are your strategy/implementation calls for your business.\n\nWeekly masterminds: You + one team member max. We keep it tight (15 members total) so everyone gets airtime and real connections form.\n\nMany members attend masterminds solo, then run internal implementation sessions with their team afterward. That rhythm works well."
              },
              {
                question: "What happens to the code if I stop my membership?",
                answer: "You keep the versions you received during your active term. Forever.\n\nYour internal-use license continues indefinitely for those versions.\n\nYou stop receiving updates. New features, improvements, and bug fixes only flow to active members.\n\nThink of it like buying software with a year of updates included. After your term, you have a snapshot you can use forever. You just don't get the new stuff."
              },
              {
                question: "How fast will I actually see ROI?",
                answer: "Most members see measurable time savings or revenue impact within 3–6 months, with ~60–90 minutes/week of focused effort.\n\nDavid's pricing calculator paid for his entire year within three months. Kuda's first AI retainer delivered over 3,200% ROI on the membership.\n\nYour results depend entirely on what you choose to build and how fast you ship. But having €50k worth of working code to start from, plus accountability to actually finish, dramatically shortens the timeline."
              },
              {
                question: "What if I miss sessions or fall behind?",
                answer: "Weekly masterminds: Recorded. Catch up async.\n\nOne-on-ones: Reschedule with reasonable notice.\n\nLife happens. We're flexible humans, not robots.\n\nThat said, momentum matters. The members who show up consistently get disproportionate results."
              },
              {
                question: "Is this only for Google Ads people?",
                answer: "No. The 8020 Agent is Google Ads-specific, but the 8020 Brain works for any business with data, decisions, and repetitive work (so... every business).\n\nCurrent members run: agencies, SaaS companies, consultancies, service businesses, e-commerce operations. The patterns translate across industries.\n\nIf you're running a business and see opportunities to build leverage with AI, you'll find value here."
              },
              {
                question: "Why only 15 people?",
                answer: "Because 30-person Zoom calls are performative theater.\n\nAt 15 people, it's small enough that:\n• You know everyone's name and business\n• You get real airtime when you're stuck\n• Relationships actually form (members help each other between calls)\n• I can track what you're working on and help you finish\n\nIt's the difference between a workshop and a working group.\n\nCurrent status: 5 of 15 spots filled. When members leave (rarely happens), spots fill from the waitlist within days."
              },
              {
                question: "What's this retreat in 2026?",
                answer: "Location TBD (likely alternating Australia/Europe annually). 2-3 days of intensive collaboration, deep-dive implementations, and in-person problem-solving.\n\nAnnual members: One ticket included\n\nSix-month members: Can purchase for €2,500 (subject to availability)\n\nMonthly members: Can purchase for €5,000 (subject to availability)\n\nThink of it as concentrated momentum that would take months over Zoom.\n\nDetails shared with members first."
              },
              {
                question: "Can I upgrade from monthly to annual later?",
                answer: "Yes. Pay the difference minus what you've already invested. Easy.\n\nThe code versions you already received are yours. You just start receiving the additional sessions and ongoing updates that come with annual membership."
              },
              {
                question: "Do you offer refunds?",
                answer: "No.\n\nThis is a serious commitment that requires investment from both sides.\n\nI'm committing to show up every week, provide guidance and shortcuts, share production codebases worth €50k+, and help you actually ship what you're capable of building.\n\nYou're committing to show up, do the work, and build.\n\nIf you're not certain this is right for your business, let's discuss it on the strategy call before you commit. But once you're in, we're both in.\n\nNo refunds. No safety nets. Just results."
              },
              {
                question: "What's the difference between Build the Agent and Own the Agent?",
                answer: "Build the Agent teaches you to create tools for yourself. It's a workshop.\n\nOwn the Agent helps you transform your entire business. It's a mastermind.\n\nBTA teaches you to fish. OTA gives you production-grade fishing equipment and helps you build a commercial fishing operation.\n\nIn OTA, you're creating AI tools that scale beyond just you. Tools for your team, your clients, maybe even tools you can package and sell. Plus, weekly accountability means far more actually gets finished.\n\nMany OTA members are Build the Agent graduates. The €500 credit recognizes that natural progression."
              }
            ].map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 dark:border-gray-700/50 rounded-lg overflow-hidden bg-white dark:bg-gray-800/50">
                <AccordionTrigger className="text-left font-semibold text-lg px-6 py-4 text-gray-900 dark:text-white hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 dark:text-gray-400 px-6 pb-5 whitespace-pre-line">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Apply Section */}
      <section className="bg-gray-50 dark:bg-gray-900 py-12 md:py-16" id="apply">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px] text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 dark:text-white scroll-animate">Apply Now</h2>
          <div className="space-y-4 text-gray-700 dark:text-gray-300 text-lg mb-8 scroll-animate">
            <p><strong>Here&apos;s what happens next:</strong></p>
            <ol className="text-left max-w-2xl mx-auto space-y-2">
              <li>1. Fill out the short application (5 minutes)</li>
              <li>2. I&apos;ll review and reach out within 48 hours</li>
              <li>3. We&apos;ll schedule a strategy call to discuss what you want to build</li>
              <li>4. If we&apos;re both excited about the fit, you choose your investment level</li>
              <li>5. You&apos;re onboarded and in the next mastermind session within a week</li>
            </ol>
            <p className="italic">No pressure. No hard sells. If it&apos;s not a fit, I&apos;ll tell you straight.</p>
          </div>
          <Button asChild size="lg" className="bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white py-6 px-12 text-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl scroll-animate">
            <Link to="/ota-application">Submit Your Application →</Link>
          </Button>
          <p className="mt-8 text-gray-600 dark:text-gray-400 scroll-animate">
            <strong>Questions before applying?</strong> Email mike@mikerhodes.com.au with &ldquo;OTA Question&rdquo; in the subject line.
          </p>
        </div>
      </section>

      {/* Final Thought */}
      <section className="bg-gradient-to-r from-indigo-700 via-blue-800 to-purple-900 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px] text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 scroll-animate">One Final Thought</h2>
          <div className="space-y-6 text-xl md:text-2xl text-white/90 scroll-animate">
            <p>Every industry has two types of professionals:</p>
            <p>Those who adapt to change when forced.</p>
            <p>And those who gleefully build the future with their own hands.</p>
            <p className="text-2xl md:text-3xl font-bold text-white pt-4">This group is for the builders.</p>
            <p>The ones who see AI not as a buzzword but as the perfect tool they&apos;ve been waiting for.</p>
            <p>The ones who automatically think &ldquo;I could build something for that.&rdquo;</p>
            <p className="text-3xl font-bold text-white pt-6">Is that you?</p>
            <p className="text-2xl">I think it might be.</p>
          </div>
          <Button asChild size="lg" className="mt-10 bg-white text-indigo-700 font-semibold hover:bg-white/90 transition-colors duration-200 shadow-xl hover:shadow-2xl text-lg transform hover:scale-105 px-12 py-6 scroll-animate">
            <Link to="/ota-application">Apply Now</Link>
          </Button>
          <p className="mt-8 text-white/80 text-sm scroll-animate">
            Own the Agent™ is intentionally limited to 15 members to ensure real transformation, not performative Zoom calls.
          </p>
          <p className="mt-4 text-white/80 scroll-animate">
            If you&apos;re reading this thinking &ldquo;I should do this someday,&rdquo; someday might be too late.
          </p>
          <p className="mt-2 text-white font-semibold scroll-animate">
            The gap between those who build and those who wait widens every month.
          </p>
          <p className="mt-6 text-2xl font-bold text-white scroll-animate">
            Stop paying for tools you don&apos;t own. Start building the ones you do.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1360px]">
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4 text-white">Own the Agent™</h3>
            <p className="text-sm mb-4">Transform your business through strategic AI implementation with guidance from Mike Rhodes.</p>
            <p className="text-xs">&copy; {new Date().getFullYear()} Own the Agent™ by Mike Rhodes. E&amp;OE. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={cn("fixed bottom-6 right-6 lg:bottom-8 lg:right-8 p-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-full shadow-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all duration-300 z-40", showBackToTop ? "opacity-100 scale-100" : "opacity-0 scale-0")}
        aria-label="Back to top"
      >
        <ArrowUp className="h-6 w-6" />
      </button>

      {/* Global styles for animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .scroll-animate {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .fade-in-up-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
